import React, { useEffect, useMemo, useState } from "react";
import { Cake, Loader2, Heart, Sparkles, BellRing } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { ensurePushSubscription, pushSupported } from "../../lib/pushClient";

/**
 * BirthdayCheerCard — interactive companion to the passive BirthdayBanner.
 *
 * When one or more campers have a birthday today, this card fetches them
 * and prompts every other member to send a real reaction — hug, high-five,
 * party emoji, cake, heart, sparkle — attributed to the birthday user so
 * the community-feel is tangible ("Marybeth got 12 cheers today!") rather
 * than the drive-by feel of a static banner line.
 *
 * Renders nothing if no birthdays today or the current user IS the only
 * birthday camper (they can't cheer themselves — instead they see a
 * dedicated "You've been cheered N times" hero via the same component).
 */

const CHEER_EMOJIS = [
  { emoji: "🎂", label: "Cake" },
  { emoji: "🥳", label: "Party" },
  { emoji: "🤗", label: "Hug" },
  { emoji: "🙌", label: "High-five" },
  { emoji: "💖", label: "Heart" },
  { emoji: "✨", label: "Sparkle" },
];

export default function BirthdayCheerCard() {
  const { user, isAuthenticated } = useAuth();
  const [list, setList] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/community/birthdays-today")
      .then(({ data }) => {
        if (!alive) return;
        // Keep only entries with a user_id — required to route cheers.
        setList((data.birthdays || []).filter((b) => b.user_id));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const meIsBirthday = useMemo(() => {
    if (!user?.user_id) return null;
    return list.find((b) => b.user_id === user.user_id) || null;
  }, [list, user?.user_id]);

  if (dismissed || list.length === 0) return null;

  // If the current user IS the birthday camper, prioritise the "you've been
  // cheered" view for them; the passive banner already handles the wish.
  if (meIsBirthday) {
    return <RecipientCheersView birthday={meIsBirthday} onDismiss={() => setDismissed(true)} />;
  }

  return (
    <div className="mt-2 space-y-3" data-testid="birthday-cheer-card">
      {list.map((b) => (
        <SenderCheerRow key={b.user_id} birthday={b} isAuthed={isAuthenticated} />
      ))}
    </div>
  );
}

/* ─────────────────────── Sender row ─────────────────────── */

function SenderCheerRow({ birthday, isAuthed }) {
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [myEmojis, setMyEmojis] = useState([]);
  const [pending, setPending] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/community/birthdays/${birthday.user_id}/cheers`);
      setCounts(data.counts_by_emoji || {});
      setTotal(data.total || 0);
      setMyEmojis(data.my_cheers || []);
    } catch { /* soft-fail */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [birthday.user_id]);

  const send = async (emoji) => {
    if (!isAuthed) {
      toast.info("Sign in to send a birthday cheer 🎂");
      return;
    }
    if (myEmojis.includes(emoji) || pending[emoji]) return;
    setPending((p) => ({ ...p, [emoji]: true }));
    // Optimistic bump so the card feels instant.
    setMyEmojis((m) => [...m, emoji]);
    setCounts((c) => ({ ...c, [emoji]: (c[emoji] || 0) + 1 }));
    setTotal((t) => t + 1);
    try {
      await api.post(`/community/birthdays/${birthday.user_id}/cheer`, { emoji });
    } catch (e) {
      // Roll back optimistic state on failure.
      setMyEmojis((m) => m.filter((x) => x !== emoji));
      setCounts((c) => ({ ...c, [emoji]: Math.max(0, (c[emoji] || 0) - 1) }));
      setTotal((t) => Math.max(0, t - 1));
      toast.error(formatErr(e));
    } finally {
      setPending((p) => { const n = { ...p }; delete n[emoji]; return n; });
    }
  };

  return (
    <section
      className="border-2 border-[#ff4500] bg-white p-4 sm:p-5 relative overflow-hidden"
      data-testid={`birthday-cheer-row-${birthday.user_id}`}
    >
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#fff4ec] to-transparent pointer-events-none" aria-hidden />
      <div className="relative flex items-start gap-3 flex-wrap">
        <div className="w-11 h-11 bg-[#ff4500] text-white rounded-full flex items-center justify-center text-2xl shrink-0" aria-hidden>
          🎂
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-0.5">
            <Cake className="w-3 h-3" /> It&rsquo;s a birthday
          </div>
          <div className="font-display text-base sm:text-lg leading-tight mb-2">
            It&rsquo;s <strong data-testid={`cheer-birthday-name-${birthday.user_id}`}>{birthday.first_name}</strong>
            {birthday.age ? ` (${birthday.age})` : ""}&rsquo;s birthday — send them a cheer around the campfire!
          </div>

          <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`cheer-emoji-row-${birthday.user_id}`}>
            {CHEER_EMOJIS.map(({ emoji, label }) => {
              const already = myEmojis.includes(emoji);
              const busy = pending[emoji];
              const n = counts[emoji] || 0;
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => send(emoji)}
                  disabled={already || busy}
                  aria-label={`Send ${label} cheer`}
                  data-testid={`cheer-btn-${birthday.user_id}-${label.toLowerCase().replace(/\W+/g, "-")}`}
                  className={
                    "inline-flex items-center gap-1 px-2.5 py-1.5 border-2 text-sm font-semibold transition-transform " +
                    (already
                      ? "border-[#ff4500] bg-[#fff4ec] text-[#ff4500] cursor-default"
                      : "border-[#e5e5e5] bg-white hover:border-[#ff4500] hover:bg-[#fff8f3] active:translate-y-[1px]")
                  }
                >
                  <span className="text-lg leading-none" aria-hidden>{emoji}</span>
                  {n > 0 && <span className="text-[11px] tabular-nums text-[#333]">{n}</span>}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="text-[11px] text-[#666] flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading cheers…
            </div>
          ) : total > 0 ? (
            <div className="text-[11px] text-[#666]">
              <strong className="text-[#0e0e0e]">{total}</strong> cheer{total === 1 ? "" : "s"} so far ·
              {myEmojis.length > 0
                ? " you've sent " + myEmojis.join(" ")
                : " tap an emoji to add yours"}
            </div>
          ) : (
            <div className="text-[11px] text-[#666]">
              Be the first to cheer {birthday.first_name} today 🎉
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── Recipient view ────────────────────── */

function RecipientCheersView({ birthday, onDismiss }) {
  const [payload, setPayload] = useState(null);
  const [pushState, setPushState] = useState(() => {
    if (!pushSupported()) return "unsupported";
    return Notification.permission === "granted" ? "on" : "prompt";
  });

  useEffect(() => {
    let alive = true;
    const fetchIt = () => api.get(`/community/birthdays/${birthday.user_id}/cheers`)
      .then(({ data }) => { if (alive) setPayload(data); })
      .catch(() => {});
    fetchIt();
    // Live-refresh every 20s so the recipient sees new cheers roll in.
    const t = setInterval(fetchIt, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [birthday.user_id]);

  // iter166 — Auto-opt the recipient into Web Push the moment they land
  // on their own birthday page and permission was already granted. If it
  // wasn't, we surface a tap-target so they can enable it in one click.
  useEffect(() => {
    if (pushState === "on") { ensurePushSubscription(); }
  }, [pushState]);

  const enablePush = async () => {
    const r = await ensurePushSubscription();
    if (r.ok) {
      setPushState("on");
      toast.success("You'll get a buzz for every new cheer 🎉");
    } else if (r.reason === "denied") {
      setPushState("denied");
      toast.error("Notifications blocked. Enable them in your browser settings.");
    } else if (r.reason === "unsupported") {
      setPushState("unsupported");
      toast.info("Your browser doesn't support push notifications.");
    } else {
      toast.error("Couldn't enable push right now — try again in a moment.");
    }
  };

  const total = payload?.total || 0;
  const recent = payload?.recent_cheers || [];

  return (
    <section
      className="mt-2 border-2 border-[#0a7f3f] bg-gradient-to-br from-[#f4fbf6] via-white to-[#fff4ec] p-5 relative"
      data-testid="birthday-recipient-view"
    >
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 text-[#888] hover:text-[#111] text-xs px-2 py-1"
        aria-label="Dismiss"
        data-testid="dismiss-birthday-recipient"
      >
        ✕
      </button>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-[#0a7f3f] text-white rounded-full flex items-center justify-center text-2xl shrink-0" aria-hidden>
          🎉
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="eyebrow text-[#0a7f3f] flex items-center gap-1.5 mb-0.5">
            <Sparkles className="w-3 h-3" /> Happy birthday, {birthday.first_name}
          </div>
          <div className="font-display text-lg sm:text-xl leading-tight mb-2">
            {total > 0 ? (
              <>The campfire is buzzing — <strong data-testid="recipient-cheer-total">{total}</strong> cheer{total === 1 ? "" : "s"} for you today 🥂</>
            ) : (
              <>Your birthday's live on the campfire — cheers will roll in through the day 🕯️</>
            )}
          </div>
          {recent.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="recipient-recent-cheers">
              {recent.slice(0, 12).map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 border hairline bg-white px-2 py-1 text-[11px] font-semibold"
                  title={`${c.from_name} sent ${c.emoji}`}
                >
                  <span className="text-base leading-none" aria-hidden>{c.emoji}</span>
                  <span className="text-[#333]">{c.from_name}</span>
                </span>
              ))}
              {recent.length > 12 && (
                <span className="inline-flex items-center px-2 py-1 text-[11px] text-[#666]">
                  <Heart className="w-3 h-3 mr-1 text-[#ff4500]" /> +{recent.length - 12} more
                </span>
              )}
            </div>
          )}

          {/* iter166 — Push opt-in row: gives the birthday camper a
              real-time buzz for every new cheer. Silent when unsupported
              or already-on. */}
          {pushState === "prompt" && (
            <button
              type="button"
              onClick={enablePush}
              className="mt-3 inline-flex items-center gap-1.5 bg-[#0a7f3f] text-white font-black text-[11px] uppercase tracking-widest px-3 py-2 hover:bg-[#0c5c30]"
              data-testid="birthday-push-enable-btn"
            >
              <BellRing className="w-3.5 h-3.5" />
              Buzz me for every new cheer
            </button>
          )}
          {pushState === "on" && (
            <div className="mt-3 text-[10px] mono uppercase tracking-widest text-[#0a7f3f] flex items-center gap-1.5">
              <BellRing className="w-3 h-3" /> Real-time buzz is on
            </div>
          )}
          {pushState === "denied" && (
            <div className="mt-3 text-[10px] mono uppercase tracking-widest text-[#999]">
              Enable notifications in your browser settings to get live cheer alerts
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
